import React from 'react';
import './styles.scss';
import { useTranslation } from 'react-i18next';
import { Bar } from 'react-chartjs-2';
import NomenclatureItem from '../NomenclatureItem/NomenclatureItem';
import { disbursementProgress } from '../../view/Common/NomenclatureItems';
import { useNumberFormatter } from '../../../../../hooks/useNumberFormatter';
import { capitalizeFirstLetter } from '../../../../../services/stringUtil';
import { DONE_DISBURSEMENT_KEY, YEAR_PROJECTION_DISBURSEMENT_KEY } from '../../utils/Constants/ProjectReportConstants';
import { getShortMonths, mapChartDisbursementAnnual } from '../../utils/Helpers/MapperHelper';

const YearDisbursements = ({ chart }) => {
    const { t } = useTranslation();
    const { formatNumber } = useNumberFormatter();

    const projectionData = mapChartDisbursementAnnual(chart.disbursementAnnualForMonths, 'actualProjectionAmountUseq');
    const disbursedData = mapChartDisbursementAnnual(chart.disbursementAnnualForMonths, 'disbursedAmountUseq');

    const maxValue = Math.max(...projectionData, ...disbursedData);
    const roundedMaxValue = Math.ceil(maxValue / 1000000) * 1000000;

    const data = {
        labels: getShortMonths(),
        datasets: [
            {
                label: t('annual_projection'),
                type: "bar",
                data: projectionData,
                backgroundColor: "#D0D5E0",
                borderColor: "#D0D5E0",
                borderWidth: 2,
                order: 2,
                barThickness: 15
            },
            {
                label: t('disbursed'),
                type: "bar",
                data: disbursedData,
                backgroundColor: "#416DBC",
                borderColor: "#416DBC",
                borderWidth: 2,
                order: 1,
                barThickness: 15
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        devicePixelRatio: 3,
        plugins: {
            datalabels: {
                display: false,
            },
        },
        scales: {
            xAxes: [
                {
                    gridLines: { display: false },
                    ticks: { fontSize: 14 }
                }
            ],
            yAxes: [
                {
                    scaleLabel: {
                        display: true,
                        labelString: 'MM ' + t('dolar_sign'),
                    },
                    gridLines: { display: true },
                    ticks: {
                        fontSize: 15,
                        beginAtZero: true,
                        stepSize: Math.ceil(roundedMaxValue / 10),
                        min: 0,
                        max: roundedMaxValue,
                        callback: (value: number) => {
                            return roundedMaxValue < 0.5 ? value : value / 1000000;
                        }
                    }
                }
            ],
        },
        legend: {
            display: false
        }
    };

    function getNomenclatureDescription(key: string): any {
        switch (key) {
            case YEAR_PROJECTION_DISBURSEMENT_KEY:
                return <strong>{t('dolar_sign')} {formatNumber(chart.totalDisbursementAnnualAmount)}</strong>;

            case DONE_DISBURSEMENT_KEY:
                return <strong>{chart.percentRealizedAnnualDisbursement}% = {t('dolar_sign')} {formatNumber(chart.realizedDisbursementAnnualAmount)}</strong>;
        }
    }

    return (
        <div className='year-disbursements-table'>
            <h3 className='chart-title'>{capitalizeFirstLetter(t('disbursements_for_the_year').toLowerCase())} (MM USD)</h3>
            <div className='chart-graph'>
                <Bar data={data} options={options} />
            </div>
            <footer className='footer-chart'>
                <NomenclatureItem
                    data={disbursementProgress.map((data) => {
                        data.description = getNomenclatureDescription(data.key!);
                        return data;
                    })} />
                <span>{t('pending')}{': '}
                    <strong>{(chart.percentPendingAnnualDisbursement).toFixed(2)}% = {t('dolar_sign')} {formatNumber(chart.pendingDisbursementAnnualAmount)}</strong>
                </span>
            </footer>
        </div>
    );
}

export default YearDisbursements;
